import React, { useCallback } from 'react';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';
import { Box, Typography } from '@mui/material';

/**
 * RichTextEditor Component
 * Simplified rich text editor for professional page content
 * Uses React-Quill with a minimal toolbar (bold, italic, underline, lists, link)
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Digite aqui...',
  maxLength = 5000,
  label = 'Conteúdo',
}) {
  // Configure Quill toolbar with only essential formatting options
  const modules = {
    toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

  // Handle change with DOMPurify sanitization
  const handleChange = useCallback(
    (newValue) => {
      // Trim HTML to check character count (excluding tags)
      const plainText = newValue.replace(/<[^>]*>/g, '').trim();

      // Check max length
      if (plainText.length <= maxLength) {
        // Sanitize HTML before passing to onChange
        const sanitized = DOMPurify.sanitize(newValue, {
          ALLOWED_TAGS: ['b', 'i', 'u', 'p', 'ol', 'ul', 'li', 'a', 'br'],
          ALLOWED_ATTR: ['href', 'target'],
        });
        onChange(sanitized);
      }
    },
    [onChange, maxLength]
  );

  // Calculate character count for display
  const plainText = value.replace(/<[^>]*>/g, '').trim();
  const characterCount = plainText.length;

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            marginBottom: '8px',
            color: 'text.primary',
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          '&:hover': {
            borderColor: '#bdbdbd',
          },
          '&:focus-within': {
            borderColor: 'primary.main',
            boxShadow: '0 0 0 3px rgba(13, 110, 253, 0.1)',
          },
          backgroundColor: '#fafafa',
          overflow: 'hidden',
        }}
      >
        <ReactQuill
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{
            height: '200px',
            backgroundColor: '#fff',
          }}
        />
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          marginTop: '8px',
          color: characterCount > maxLength * 0.9 ? '#d32f2f' : '#757575',
          textAlign: 'right',
        }}
      >
        {characterCount} / {maxLength} caracteres
      </Typography>
    </Box>
  );
}
